import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Home, UserCircle, Building2 } from 'lucide-react';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function AuthPage() {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'tenant',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password, otp: twoFAStep ? otp : undefined }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);

      // Check if 2FA is required
      if (response.data.twofa_required) {
        setTwoFAStep(true);
        toast.info('Enter the 6-digit code from your authenticator app');
        setLoading(false);
        return;
      }

      const { access_token, user } = response.data;
      login(access_token, user);
      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');

      if (user.role === 'landlord') navigate('/landlord/dashboard');
      else if (user.role === 'employee') navigate('/landlord/dashboard');
      else if (user.role === 'tenant') navigate('/tenant/dashboard');
      else if (user.role === 'admin') navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelTwoFA = () => {
    setTwoFAStep(false);
    setOtp('');
    setFormData({ ...formData, password: '' });
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    sessionStorage.setItem('pending_google_role', formData.role || 'tenant');
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleAppleSignIn = () => {
    // Emergent-managed Apple Sign-In not yet available; provider selector isn't supported by /?redirect=
    // This surfaces a clear "coming soon" until Apple Dev credentials are wired.
    toast.info(
      'Apple Sign-In is coming soon. Please use email/password or Google for now.',
      { duration: 6000 }
    );
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      toast.error('Enter your email first, then click "Forgot password?"');
      return;
    }
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: formData.email });
      toast.success(
        'If that email is registered, a reset link is on its way. Check your inbox.',
        { duration: 8000 }
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset link');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4 py-6 sm:px-6 sm:py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Home className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-primary">PrimePocket</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">
            {isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm sm:text-base">
            {isLogin ? 'Sign in to your account' : 'Get started with PrimePocket'}
          </p>
        </div>

        <Card className="p-5 sm:p-8 border border-border shadow-sm">
          {twoFAStep ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-4">
                <h2 className="text-xl font-medium mb-2">Two-Factor Authentication</h2>
                <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app</p>
              </div>
              <div>
                <Label htmlFor="otp">Authentication Code</Label>
                <Input
                  id="otp"
                  data-testid="login-otp-input"
                  type="text"
                  required
                  autoFocus
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="mt-2 text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
              <Button
                type="submit"
                data-testid="login-verify-otp-btn"
                disabled={loading || otp.length !== 6}
                className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover"
              >
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </Button>
              <button
                data-testid="login-cancel-2fa-btn"
                type="button"
                onClick={handleCancelTwoFA}
                className="w-full text-sm text-muted-foreground hover:text-primary"
              >
                Use a different account
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="auth-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    data-testid="auth-phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label>I am a...</Label>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <button
                      type="button"
                      data-testid="auth-role-tenant-btn"
                      onClick={() => setFormData({ ...formData, role: 'tenant' })}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        formData.role === 'tenant'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <UserCircle className="w-6 h-6 mx-auto mb-2" />
                      <div className="text-sm font-medium">Tenant</div>
                    </button>
                    <button
                      type="button"
                      data-testid="auth-role-landlord-btn"
                      onClick={() => setFormData({ ...formData, role: 'landlord' })}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        formData.role === 'landlord'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Building2 className="w-6 h-6 mx-auto mb-2" />
                      <div className="text-sm font-medium">Landlord</div>
                    </button>
                  </div>
                </div>
              </>
            )}

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              data-testid="google-signin-btn"
              onClick={handleGoogleSignIn}
              variant="outline"
              className="w-full rounded-full btn-hover flex items-center gap-3"
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </Button>

            <Button
              type="button"
              data-testid="apple-signin-btn"
              onClick={handleAppleSignIn}
              variant="outline"
              className="w-full rounded-full btn-hover flex items-center gap-3 mt-2 bg-black hover:bg-neutral-800 text-white border-black"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.5 12.5c0-3 2.4-4.4 2.5-4.5-1.4-2-3.5-2.3-4.3-2.3-1.8-.2-3.6 1.1-4.5 1.1-.9 0-2.4-1.1-4-1-2 0-3.9 1.2-4.9 3-2.1 3.7-.5 9.1 1.5 12.1 1 1.5 2.2 3.1 3.8 3 1.5-.1 2.1-1 3.9-1s2.4 1 4 .9c1.6 0 2.7-1.5 3.7-2.9 1.2-1.7 1.7-3.4 1.7-3.5-.1-.1-3.3-1.3-3.4-4.9zM14.5 3.5c.8-1 1.4-2.4 1.2-3.7-1.2 0-2.6.8-3.4 1.8-.8.9-1.5 2.3-1.3 3.6 1.3.1 2.7-.7 3.5-1.7z"/>
              </svg>
              <span>Continue with Apple</span>
            </Button>

            {isLogin && (
              <div className="text-center mt-2">
                <button
                  data-testid="forgot-password-link"
                  type="button"
                  onClick={handleForgotPassword}
                  className="text-sm text-muted-foreground hover:text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
          </form>
          )}

          {!twoFAStep && (
          <div className="mt-6 text-center">
            <button
              data-testid="auth-toggle-btn"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-sm text-primary hover:underline"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
          )}
        </Card>
      </div>
    </div>
  );
}
